import {rootState} from '@mintdata-wsp/common/state/RootState';
import {VisualComponent} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/VisualComponent';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {
    DrawingComponentState,
    SurfacePoint,
    SurfaceSize
} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Bounds} from '@mintdata/mint-kit/api/models/Bounds';
import {action} from 'mobx';

export class SurfaceDrawingComponentController
{
    @action
    public enableDrawingComponent(component:VisualComponent):void
    {
        const {surfaceSelection} = controllers.ui;
        const state = this._getState();

        surfaceSelection.clearSelectedBoxesForPage();

        state.drawingComponentEnabled = true;
        state.drawingComponent = component;
    }

    @action
    public disableDrawingComponent():void
    {
        const state = this._getState();

        this.clearDrawingComponentBounds();

        state.drawingComponentEnabled = false;
        state.drawingComponent = null;
        state.drawingStartedPoint = null;
    }

    @action
    public setDrawingComponentBounds(bounds:Bounds):void
    {
        const state = this._getState();

        state.drawingBounds = bounds;
    }

    @action
    public setDrawingStartedPoint(point:SurfacePoint):void
    {
        const state = this._getState();

        state.drawingStartedPoint = point;
    }

    @action
    public clearDrawingComponentBounds():void
    {
        const state = this._getState();

        state.drawingBounds = null;
    }

    @action
    public drawComponent():void
    {
        const {surfaceArea, surfaceSelection} = controllers.ui;
        const state = this._getState();

        const page:Page = surfaceArea.getCurrentLayerPage();
        let parentBox:Box = null;

        const component:VisualComponent = state.drawingComponent;
        const bounds:Bounds = this.getDrawingComponentBounds(true);

        let position:SurfacePoint = SurfacePoint.from(bounds.x, bounds.y);
        const size:SurfaceSize = SurfaceSize.from(bounds.width, bounds.height);

        const viewport:Box = surfaceArea.getCurrentLayerPage().artboard.viewport;

        if( surfaceArea.hasSurfacePointIntersectionWithBounds(this.getDrawingStartedPoint(), viewport.bounds) )
        {
            parentBox = viewport;
            position = SurfacePoint.from(bounds.x - viewport.bounds.x, bounds.y - viewport.bounds.y);
        }

        surfaceArea.addNewBox(
            page,
            parentBox,
            null,
            component,
            null,
            position,
            size,
            (box:Box) =>
            {
                surfaceSelection.setSingleBoxSelection(box);
                surfaceArea.focusSurfaceArea();
            }
        );

        this.disableDrawingComponent();
    }

    public isDrawingComponentEnabled():boolean
    {
        const state = this._getState();

        return state.drawingComponentEnabled;
    }

    public getDrawingComponentBounds(adjustNegative:boolean = false):Bounds | null
    {
        const state = this._getState();

        const bounds:Bounds = state.drawingBounds;

        if( bounds && adjustNegative )
        {
            let {x, y, width, height} = bounds;

            if( width < 0 )
            {
                x += width;
                width = Math.abs(width);
            }

            if( height < 0 )
            {
                y += height;
                height = Math.abs(height);
            }

            return Bounds.from(x, y, width, height);
        }

        return bounds;
    }

    public getDrawingStartedPoint():SurfacePoint
    {
        const state = this._getState();

        return state.drawingStartedPoint;
    }

    public _getState():DrawingComponentState
    {
        return rootState.ui.visualDesigner.surfaceEditor.area.drawingComponent;
    }
}
